// src/components/navigation/MainMenu.tsx
import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { resolveCrossAppUrl } from '../../utils/crossAppUrlResolver';
import MenuTrigger from './shared/MenuTrigger';
import MenuItem from './shared/MenuItem';
import './MainMenu.scss';

interface MenuItemData {
  type: 'link' | 'group' | 'external' | 'cross-app';
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: string;
  children?: MenuItemData[];
}

interface MainMenuProps {
  menu: MenuItemData[];
}

const MainMenu: React.FC<MainMenuProps> = ({ menu }) => {
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const renderItems = (items: MenuItemData[]) =>
    items.map((item, idx) => {
      // external link item — render as a native anchor with target="_blank"
      // so the browser handles new-tab semantics directly (window.open is
      // unreliable inside the CMS iframe and blocked by popup blockers when
      // fired through wrapped click chains; native <a> sidesteps both).
      if (item.type === 'external' && item.href) {
        return (
          <a
            key={idx}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="shared-menu-item"
            onClick={() => setOpen(false)}
          >
            {item.icon && <i className={item.icon} style={{ marginRight: '8px' }} />}
            {item.label}
          </a>
        );
      }

      // cross-app link item (opens in same tab for shared auth)
      if (item.type === 'cross-app') {
        return (
          <MenuItem
            key={idx}
            onClick={() => {
              if (item.href) {
                const resolvedUrl = resolveCrossAppUrl(item.href);
                if (resolvedUrl) {
                  window.location.href = resolvedUrl;
                } else {
                  console.error(`[MainMenu] Failed to resolve cross-app URL: ${item.href}`);
                }
              }
              // close menu after click
              setOpen(false);
            }}
          >
            {item.icon && <i className={item.icon} style={{ marginRight: '8px' }} />}
            {item.label}
          </MenuItem>
        );
      }

      // group item
      if (item.type === 'group') {
        const isOpen = openGroups[item.label];
        return (
          <div key={idx} className="mainmenu-group-item">
            <MenuItem onClick={() => toggleGroup(item.label)}>
              <FontAwesomeIcon
                icon={isOpen ? faChevronDown : faChevronRight}
                className="mainmenu-group-toggle"
              />
              <span className="mainmenu-group-label">{item.label}</span>
            </MenuItem>
            {isOpen && (
              <div className="mainmenu-group">
                {renderItems(item.children || [])}
              </div>
            )}
          </div>
        );
      }

      // plain link item
      return (
        <MenuItem
          key={idx}
          onClick={() => {
            if (item.onClick) {
              item.onClick();
            }
            // close menu after any normal item click
            setOpen(false);
          }}
        >
          {item.icon && <i className={item.icon} style={{ marginRight: '8px' }} />}
          {item.label}
        </MenuItem>
      );
    });

  return (
    <div className="mainmenu-container" ref={ref}>
      <MenuTrigger
        label="Menu"
        icon={faBars}
        isOpen={open}
        onClick={() => setOpen((p) => !p)}
      />
      {open && <div className="mainmenu-dropdown">{renderItems(menu)}</div>}
    </div>
  );
};

export default MainMenu;
